import { computed } from "vue";
import type { LocationAsRelativeRaw } from "vue-router";
import { defineStore, storeToRefs } from "pinia";
import { DashboardPermission } from "@/helpers/apiDashboardWrappers";
import { getExtLinks } from "@/helpers/commonHelpers";
import { useLicenseRequestStore } from "./licenseRequestsStore";
import { useUserAuthorizationStore } from "./userAuthorizationStore";
import { useAccountStore } from "./accountStore";

export interface LinkData {
  text: string;
  routeTo?: LocationAsRelativeRaw;
  href?: string;
  badge?: string;
  inNewTab?: boolean;
}

export const useSidebarStore = defineStore('sidebar', () => {
  const extLinks = getExtLinks();

  const { loadRequests } = useLicenseRequestStore();
  const { requestsCount, requests } = storeToRefs(useLicenseRequestStore());
  const { permissions } = storeToRefs(useUserAuthorizationStore());
  const { currentAccount } = storeToRefs(useAccountStore());

  const nonAdminLinks = computed(() => {
    return [
      { text: "Overview", routeTo: { name: "Overview" } },
      { text: "Support Cases", href: `${extLinks.supportCenterUrl}/#MyCases` },
    ] as LinkData[]
  });

  const isOrgAdmin = computed(() => permissions.value.includes(DashboardPermission.CompanyProfileEdit));

  const regularLinks = computed(() => {
    const links: LinkData[] = [
      { text: "Users and Groups", href: `${extLinks.accountBaseUrl}/users-and-groups` },
      { text: "Licenses", routeTo: { name: "SubscrsLicenses" }, badge: requestsCount.value?.toString() },
      { text: "Workspaces", href: `${extLinks.accountBaseUrl}/workspaces` },
      { text: "Payment and Invoices", routeTo: { name: "PaymentInvoices" } },
      { text: "Reports", routeTo: { name: "Reports" } },
      { text: "Company Profile", routeTo: { name: "CompanyProfile" } },
      { text: "Authentication", href: `${extLinks.accountBaseUrl}/authentication` },
      { text: "Community Forum", href: extLinks.forumUrl, inNewTab: true },
      { text: "Support Cases", href: `${extLinks.supportCenterUrl}/#MyCases` },
    ];

    if (currentAccount.value?.content?.advancedSecurityPackages?.length) {
      links.push({ text: "Advanced Controls ", href: `${extLinks.accountBaseUrl}/advanced-controls` });
    }

    return links as LinkData[];
  });

  const sidebarLinks = computed(() => {
    if (!permissions.value.includes(DashboardPermission.LicenseListView)) {
      return nonAdminLinks.value;
    }

    if (isOrgAdmin.value) {
      return [{ text: "Overview", routeTo: { name: "Overview" } }, ...regularLinks.value];
    }

    return regularLinks.value;
  })

  async function init() {
    if (!requests.value.result && permissions.value.includes(DashboardPermission.LicenseRequestsView)) {
      loadRequests();
    }
  }

  return { sidebarLinks, init };
});
